import {
    ClassificationV4DTO,
    PageMetaDTO,
} from '@west-australian-newspapers/publication-types'
import H from 'history'
import {
    CheckedCompositionInformation,
    getRegistrationCreators,
    LayoutApi,
} from 'json-react-layouts'
import { init } from 'json-react-layouts-data-loader'
import { AdState } from '../advertising/ad-state'
import { AdPageKind } from '../advertising/ad-targeting'
import { AdTargeting } from '../advertising/add-ads'
import { GptApi } from '../advertising/gpt-api'
import { CardItem } from '../client-data-types/card-types'
import { Topic } from '../client-data-types/topic'
import { RenderTarget } from '../context-providers/render-target-context'
import { DataLoaderGlobalParams, resources } from '../data/resources'
import { AllEvents } from '../events/all-events'
import { RedirectTo, RouteResolution } from '../routing/route-resolver'
import { cacheDataLoad } from './RoutePrimer'

export interface AggregatedData {
    type: 'aggregated'
    sources: Array<CurationData | ListingData | GeolocationCurationData>
    /** The number of items from the start to skip. */
    offset?: number
    /** The maximum number of items to return. */
    maxPageSize?: number
    /** Don't de-duplicate results. Defaults to false. */
    keepDuplicates?: boolean
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
        /** The maximum number of paginated pages we want to display. */
        maxPageNumber?: number
    }
}

export interface CurationData {
    type: 'curation'
    name: string
    /* The number of items in the curation to skip */
    offset: number
    /** The number of items to return */
    pageSize: number
    hideProfiles?: boolean
    /** Allows a story stored in metadata to be injected into the collection */
    injectMetadataStory?: {
        /** Index the story will be injected into */
        position: number
        metadataKey: string
        /** Controls what card type it will be mapped to before injecting */
        kind: CardItem['cardType']
    }
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
    /* Allows filtering out results that exist in other curations */
    excludeCurations?: string[]
    /* Allows filtering out results by source */
    excludeSources?: string[]
    /* Allows filtering out results by trending stories exclusion */
    applyTrendingStoriesExclusion?: boolean
}

export interface ListingData {
    type: 'listing'
    source?: string
    topics: string[]
    authors?: string[]
    includeSubTopics?: boolean
    excludeTopics?: string[]
    paging: {
        /** page is 1 based numbering */
        page: number
        pageSize: number
        pageOffset?: number | undefined
        /** The maximum number of paginated pages we want to display. */
        maxPageNumber?: number
    }
    /** By default listing results are in a reverse chron so latest first order, this orders the results in chronological order */
    sortResultsInChornologicalOrder?: boolean
    /** Filters to sponsored content */
    isSponsored?: boolean
    publicationKind?: 'article' | 'gallery' | 'galleryAndArticle'
    hideProfiles?: boolean
    classification?: ClassificationV4DTO
    random?: boolean
    random_offset?: number
    random_size?: number
    seed?: string
    excludeSubTopics?: boolean
    total?: number
}

export interface VideoListingData {
    type: 'video-listing'
    topics: string[]
    paging: {
        /** page is 1 based numbering */
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export interface GeolocationCurationData {
    type: 'geolocation'
    /* The number of items in the curation to skip */
    offset?: number
    /** The number of items to return */
    pageSize: number
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export interface PromotionalData {
    type: 'promotional'
    // Will default to promotion-cards if not specified
    curationId?: string
    metadataKey: string
    pageSize: number
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export interface ActivePromotionData {
    type: 'active-promotion'
    promotionSlotId: string
    metadataKey?: string
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export interface ContextData {
    type: 'context'
    /** The id of the CollectionDataContext that is responsible for storing this data. */
    contextId: string
    /** The same as the name value consumed by a CurationData. This is the section id. */
    section?: string
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export interface EditionData {
    type: 'edition'
    pageSize: number
    page: number
    total?: number
    throw404OnNoResults?: boolean
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
        /** The maximum number of paginated pages we want to display. */
        maxPageNumber?: number
    }
}

export interface NewsTickerData {
    type: 'news-ticker'
    total: number
    pageSize: number
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export interface NotificationCentreData {
    type: 'notification-centre'
    total: number
    pageSize: number
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export interface ElectionData {
    type: 'election'
    electionId: string
    pageSize: number
    paging?: {
        page: number
        pageSize: number
        pageOffset?: number | undefined
    }
}

export type RouteMetaPageTypes =
    | 'homepage'
    | 'topic'
    | 'video-series'
    | 'static'
    | 'publication'
    | 'page'
    | 'test'
    | 'error'
    | 'rss'
    | 'profile'
    | 'search'
    | 'simple-publication'
    | 'breach'

// This is only METADATA about the data required for this layout, not the data itself
// We can preload this information and we should try to keep this data
// structure as light as possible
export interface RouteMeta<Section extends string> {
    /**
     * Page heading / page title, used as the meta title if not overridden and the page heading
     **/
    heading: string

    /** Opt out of the default title template which is %s | ${Section} */
    noMetaTitleTemplate?: boolean

    /** Formats page meta like so: `${pageMeta.description} from ${sectionInfo.Title}` */
    includeFromSectionInMetaDescription?: boolean

    headerHasBackgroundFill?: boolean
    /* determines if heading (title) is hidden on the page, default is shown */
    hideHeading?: boolean
    /* The page type which will be reported to GA */
    pageType: RouteMetaPageTypes
    refreshOnInactive?: boolean
    adTargeting: AdTargeting
    pageMeta?: PageMetaDTO
    section: Section
    socialMeta?: {
        title?: string
        description?: string
    }
    /** Additional props which should be inlcuded in the page loaded event */
    additionalPageProperties?: object
}

export interface PageRouteInformation<Sections extends string>
    extends RouteMeta<Sections> {
    kind: 'page'
    compositions: Array<CheckedCompositionInformation>
}
export interface RedirectRouteInformation {
    kind: 'redirect'
    isErrorPage?: boolean
    redirectTo: RedirectTo
}

export interface LoadingRouteInformation {
    kind: 'loading'
    section: string | undefined
}

export type RouteInformation<Sections extends string> =
    | PageRouteInformation<Sections>
    | RedirectRouteInformation
    | LoadingRouteInformation

export type GetRouteAdTargeting = (
    adPageKind: AdPageKind,
    section: string,
    primaryTopicOrPageId: string | Topic,
    ...secondaryTopics: Topic[]
) => AdTargeting

/** Available to route info resolvers */
export interface RouteServices<
    Resolution extends RouteResolution = RouteResolution,
> extends DataLoaderGlobalParams {
    getAdTargeting: GetRouteAdTargeting
    resolution: Resolution
    location: H.Location
    hostname: string | undefined
    /**
     * On server: Should be `${x-forwarded-proto}:` - not the service protocol which will always be http
     * On client: window.location.protocol
     */
    protocol: string | undefined
    renderTarget: RenderTarget
    gptApi: GptApi
}

/** Available to all registered components at render time */
export interface ComponentServices<
    Resolution extends RouteResolution = RouteResolution,
> extends DataLoaderGlobalParams {
    layout: LayoutApi<any, any, ComponentServices, any, any>
    resolution: Resolution
    location: H.Location
    adState: AdState
    onEvent: (event: AllEvents) => void
}

export type StaticRoute<
    Sections extends string = string,
    Resolution extends RouteResolution = RouteResolution,
> = (services: RouteServices<Resolution>) => RouteInformation<Sections> | null

export type StaticPageRoute<
    Sections extends string = string,
    Resolution extends RouteResolution = RouteResolution,
> = (services: RouteServices<Resolution>) => PageRouteInformation<Sections>

export interface StaticRoutes<
    Sections extends string = string,
    Resolution extends RouteResolution = RouteResolution,
> {
    [key: string]: StaticRoute<Sections, Resolution>
}

// Exports
export type PageType<Sections extends string> = PageRouteInformation<Sections>

export const { createRegisterableComponent, createRegisterableComposition } =
    getRegistrationCreators<ComponentServices>()

export const {
    createRegisterableComponentWithData,
    createRegisterableCompositionWithData,
    getMiddleware: getDataLoaderMiddleware,
} = init<ComponentServices>(resources, cacheDataLoad)
